import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Search, X, Award, Wallet, Lock, TrendingUp, Image as ImageIcon, Users, Receipt } from "lucide-react";
import StatCard from "../components/agentProgram/StatCard";
import StatusPill from "../components/agentProgram/StatusPill";
import EmptyState from "../components/agentProgram/EmptyState";

const API = process.env.REACT_APP_API_URL;
const SERVER_ROOT = (API || "").replace(/\/api\/?$/, "");

const requestStatusLabel = {
  pending: "Pending",
  fulfilled: "Fulfilled",
  dismissed: "Dismissed",
};

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "-";

const statusLabel = {
  scheduled: "Scheduled",
  payable: "Payable",
  paid: "Paid",
  held: "Held",
  released: "Released",
  clawed_back: "Clawed back",
  forfeited: "Forfeited",
};

export default function AgentWallet() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [levels, setLevels] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState({ requests: [], releaseBatches: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allAgents, setAllAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const searchBoxRef = useRef(null);

  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  // Default landing state — browse every agent instead of showing nothing
  // until a search is typed.
  useEffect(() => {
    const loadAgents = async () => {
      setLoadingAgents(true);
      try {
        const { data } = await axios.get(`${API}/agent-program/agents`, authConfig);
        setAllAgents(data || []);
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to load agents");
      } finally {
        setLoadingAgents(false);
      }
    };
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/merchants`, {
          ...authConfig,
          params: { q: searchTerm.trim(), limit: 8 },
        });
        setSearchResults(data?.items || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const loadWallet = async (agentId) => {
    setError("");
    setLoading(true);
    try {
      const [{ data: summaryData }, { data: txnData }, { data: configData }, { data: historyData }] = await Promise.all([
        axios.get(`${API}/agent-program/agents/${agentId}/summary`, authConfig),
        axios.get(`${API}/agent-program/agents/${agentId}/transactions`, authConfig),
        axios.get(`${API}/agent-program/config`, authConfig),
        axios.get(`${API}/agent-program/agents/${agentId}/payout-history`, authConfig),
      ]);
      setSummary(summaryData);
      setTransactions(txnData || []);
      setLevels((configData?.levels || []).slice().sort((a, b) => a.level - b.level));
      setPayoutHistory({ requests: historyData?.requests || [], releaseBatches: historyData?.releaseBatches || [] });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load agent wallet");
    } finally {
      setLoading(false);
    }
  };

  const selectAgent = (merchant) => {
    setSelectedAgent(merchant);
    setSearchTerm("");
    setSearchResults([]);
    setShowResults(false);
    loadWallet(merchant._id);
  };

  const clearAgent = () => {
    setSelectedAgent(null);
    setSummary(null);
    setTransactions([]);
    setLevels([]);
    setPayoutHistory({ requests: [], releaseBatches: [] });
    setError("");
  };

  const s = summary || {};

  let progressCaption = "";
  const currentIdx = levels.findIndex((l) => l.level === s.level);
  if (currentIdx >= 0 && currentIdx < levels.length - 1) {
    const remaining = Math.max(0, levels[currentIdx + 1].minPoints - (s.unlockedPoints || 0));
    progressCaption = `${remaining} pts to Level ${levels[currentIdx + 1].level}`;
  } else if (currentIdx === levels.length - 1 && levels.length > 0) {
    progressCaption = "Top level reached";
  }

  const payoutTimeline = [
    ...payoutHistory.requests.map((r) => ({ kind: "request", at: r.requestedAt, data: r })),
    ...payoutHistory.releaseBatches.map((b) => ({ kind: "release", at: b.releasedAt, data: b })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Wallet size={20} className="text-blue-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Agent Wallet</h1>
          <p className="text-sm text-gray-500 mt-0.5">Browse any agent's level, points progress, and balances.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-5">
        {selectedAgent ? (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">{selectedAgent.name}</div>
              {selectedAgent.identifier && (
                <div className="text-xs text-gray-500">{selectedAgent.identifier}</div>
              )}
            </div>
            <button
              onClick={clearAgent}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 px-3 py-1.5 rounded-lg border border-gray-200"
            >
              <X size={14} /> Change agent
            </button>
          </div>
        ) : (
          <div className="relative" ref={searchBoxRef}>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="border border-gray-200 rounded-lg text-sm pl-9 pr-3 py-2 w-full"
                placeholder="Search agent by name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowResults(true);
                }}
                onFocus={() => setShowResults(true)}
              />
            </div>
            {showResults && searchTerm.trim() && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {searching ? (
                  <div className="px-4 py-3 text-sm text-gray-400">Searching…</div>
                ) : searchResults.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400">No agents match "{searchTerm}"</div>
                ) : (
                  searchResults.map((m) => (
                    <button
                      key={m._id}
                      onClick={() => selectAgent(m)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <div className="text-sm text-gray-800">{m.name}</div>
                      {(m.identifier || m.contactNumber) && (
                        <div className="text-xs text-gray-400">
                          {[m.identifier, m.contactNumber].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}

      {!selectedAgent && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-5">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2.5">
            <Users size={17} className="text-gray-400" />
            <div>
              <h3 className="text-sm font-semibold text-gray-800">All Agents</h3>
              <p className="text-xs text-gray-400 mt-0.5">{allAgents.length} agent{allAgents.length !== 1 ? "s" : ""} in the program</p>
            </div>
          </div>
          {loadingAgents ? (
            <EmptyState icon={Users} title="Loading agents…" compact />
          ) : allAgents.length === 0 ? (
            <EmptyState icon={Users} title="No agents yet" compact />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    {["Agent", "Level", "Available", "Held", "KYC"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {allAgents.map((row) => (
                    <tr
                      key={row.agent?._id || row._id}
                      onClick={() => row.agent && selectAgent(row.agent)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <div className="text-gray-800 font-medium">{row.agent?.name || "—"}</div>
                        {row.agent?.identifier && <div className="text-xs text-gray-400">{row.agent.identifier}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">Level {row.level ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-blue-600 whitespace-nowrap">₹{money(row.availableBalance)}</td>
                      <td className="px-4 py-3 text-sm text-amber-600 whitespace-nowrap">₹{money(row.heldBalance)}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <StatusPill label={row.kycVerified ? "Verified" : "Pending"} tone={row.kycVerified ? "success" : "warning"} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selectedAgent && loading && (
        <div className="text-center text-gray-400 text-sm py-12">Loading…</div>
      )}

      {selectedAgent && !loading && summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-3">
            <StatCard
              title="Level"
              value={`Level ${s.level ?? 0}`}
              helper={progressCaption}
              icon={Award}
              tone="bg-indigo-50 text-indigo-600"
            />
            <StatCard
              title="Unlocked Points"
              value={s.unlockedPoints ?? 0}
              helper={`${s.lockedPoints ?? 0} pts locked`}
              icon={TrendingUp}
              tone="bg-emerald-50 text-emerald-600"
            />
            <StatCard
              title="Available Balance"
              value={`₹${money(s.availableBalance)}`}
              helper="Withdrawable now"
              icon={Wallet}
              tone="bg-blue-50 text-blue-600"
            />
            <StatCard
              title="Held Balance"
              value={`₹${money(s.heldBalance)}`}
              helper="Releases automatically 6 months after disbursal"
              icon={Lock}
              tone="bg-amber-50 text-amber-600"
            />
          </div>

          {s.payoutRequestedAt && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-4 py-3 text-sm mb-3">
              This agent requested a payout on {dateText(s.payoutRequestedAt)} — see Payout Requests to release it.
            </div>
          )}

          {s.outstandingShortfall > 0 && (
            <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-4 py-3 text-sm mb-5">
              Outstanding shortfall of ₹{money(s.outstandingShortfall)} from a prior clawback is being recovered
              from future earnings before new payouts are released.
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Recent Transactions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    {["#", "Type", "Gross", "TDS", "Net", "Available", "Held", "Status", "Date"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <EmptyState icon={Receipt} title="No transactions yet" compact />
                      </td>
                    </tr>
                  ) : (
                    transactions.map((txn) => (
                      <tr key={txn._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{txn.serialNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap capitalize">
                          {txn.type.replace(/_/g, " ")}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">₹{money(txn.grossAmount)}</td>
                        <td className="px-4 py-3 text-sm text-red-500 whitespace-nowrap">₹{money(txn.tdsAmount)}</td>
                        <td className="px-4 py-3 text-sm text-gray-800 font-medium whitespace-nowrap">₹{money(txn.netAmount)}</td>
                        <td className="px-4 py-3 text-sm text-blue-600 whitespace-nowrap">₹{money(txn.availableAmount)}</td>
                        <td className="px-4 py-3 text-sm text-amber-600 whitespace-nowrap">
                          ₹{money(txn.heldAmount)}
                          {txn.heldReleaseDate && txn.status === "held" && (
                            <span className="block text-[11px] text-gray-400">
                              releases {dateText(txn.heldReleaseDate)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap capitalize">
                          {statusLabel[txn.status] || txn.status}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{dateText(txn.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0 mt-5">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">Payout History</h3>
            </div>
            {payoutTimeline.length === 0 ? (
              <EmptyState icon={ImageIcon} title="No payout activity yet" compact />
            ) : (
              <div className="divide-y divide-gray-100">
                {payoutTimeline.map((item, idx) =>
                  item.kind === "request" ? (
                    <div key={`req-${item.data._id || idx}`} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-800">Requested ₹{money(item.data.requestedAmount)}</div>
                        <div className="text-xs text-gray-400">{dateText(item.data.requestedAt)}</div>
                      </div>
                      <StatusPill
                        label={requestStatusLabel[item.data.status] || item.data.status}
                        tone={item.data.status === "pending" ? "warning" : item.data.status === "fulfilled" ? "success" : "neutral"}
                      />
                    </div>
                  ) : (
                    <div key={`rel-${item.data._id || idx}`} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-800">
                          Released ₹{money(item.data.netAmount)}
                          {item.data.shortfallRecovered > 0 && (
                            <span className="text-xs text-orange-500"> (₹{money(item.data.shortfallRecovered)} recovered from shortfall)</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          batch #{item.data.serialNumber} · by {item.data.releasedBy?.name || "admin"} · {dateText(item.data.releasedAt)}
                        </div>
                      </div>
                      {item.data.screenshotUrl && (
                        <a
                          href={`${SERVER_ROOT}${item.data.screenshotUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <ImageIcon size={14} /> View proof
                        </a>
                      )}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
